import { BASE_URL } from './constants';
import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';
import type { Campaign } from './campaigns';
import type { OnboardingSignalsSaveRequest } from '../components/onboarding/onboardingSaveRequest';

/**
 * Generates ICP account suggestions for the given ideal customer description.
 *
 * Backend route and request/response schema are based on current testing needs:
 * - Request: ideal_customer_description (TBA if backend expects a different key)
 * - Response: JSON with `accounts`: [{ company_name, website_domain }]
 * - 400 when the description does not yield enough accounts (detail message).
 */
export type ICPAccount = {
  company_name: string;
  website_domain: string;
};

/** Onboarding error bodies are always `application/json` as `{ "detail": "<message>" }`. */
async function parseOnboardingErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  try {
    const data = (await response.json()) as { detail: string };
    return data.detail;
  } catch {
    return fallback;
  }
}

export async function generateICPAccounts(
  idealCustomerDescription: string,
  signal?: AbortSignal
): Promise<ICPAccount[]> {
  const userSessionToken = getUserSessionCookie();
  const url = `${BASE_URL}/v1/onboarding/icp/accounts/generate`;

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify({
      // Key name may need to change if/when backend contract is finalized.
      icp_text: idealCustomerDescription.trim()
    }),
    signal
  });

  if (!response.ok) {
    const message = await parseOnboardingErrorMessage(
      response,
      `Could not save your answer (${response.status})`
    );
    throw new Error(message);
  }

  const data = await response.json();
  const accountData = data.accounts;

  return accountData as ICPAccount[];
}

function parseSignalsResultInstructions(data: unknown): string[] {
  const instructions = (data as { instructions: string[] }).instructions;
  return instructions;
}

/**
 * Expands free-text signals into backend GTM instruction strings.
 *
 * POST /v1/onboarding/signals/generate
 * - Request: { signals_intent_text: string }
 * - Response: `SignalsResult` — `{ instructions: string[] }`
 * - 400 when invalid / not enough signal detail (detail message), same pattern as ICP.
 */
export async function generateSignalInstructions(
  signalsText: string,
  signal?: AbortSignal
): Promise<string[]> {
  const userSessionToken = getUserSessionCookie();
  const url = `${BASE_URL}/v1/onboarding/signals/generate`;

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify({ signals_intent_text: signalsText.trim() }),
    signal
  });

  if (!response.ok) {
    const message = await parseOnboardingErrorMessage(
      response,
      `Could not generate signal instructions (${response.status})`
    );
    throw new Error(message);
  }

  const data = await response.json();
  return parseSignalsResultInstructions(data);
}

/**
 * Validates prioritization free text and returns a single-line summary for persistence.
 *
 * POST /v1/onboarding/prioritization/validate
 * - Request: { prioritization_text: string }
 * - Response: { message: string }
 * - 400 with detail when invalid.
 */
export async function validatePrioritizationCriteria(
  prioritizationText: string,
  signal?: AbortSignal
): Promise<string> {
  const userSessionToken = getUserSessionCookie();
  const url = `${BASE_URL}/v1/onboarding/prioritization/validate`;

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify({ prioritization_text: prioritizationText.trim() }),
    signal
  });

  if (!response.ok) {
    const message = await parseOnboardingErrorMessage(
      response,
      `Could not validate prioritization (${response.status})`
    );
    throw new Error(message);
  }

  const data = (await response.json()) as { message: string };
  return data.message;
}

type OnboardingSaveSseEvent =
  | { type: 'campaign_created'; campaign: Campaign }
  | { type: 'heartbeat' }
  | {
    type: 'setup_complete';
    campaign: Campaign;
    cell_orchestrator_workflow_id?: string;
  }
  | { type: 'setup_failed'; detail: string };

function handleOnboardingSseData(jsonStr: string): Campaign | 'pending' {
  const event = JSON.parse(jsonStr) as OnboardingSaveSseEvent;
  switch (event.type) {
    case 'heartbeat':
    case 'campaign_created':
      return 'pending';
    case 'setup_failed':
      throw new Error(event.detail);
    case 'setup_complete':
      return event.campaign;
  }
}

async function readOnboardingSaveSse(
  body: ReadableStream<Uint8Array> | null
): Promise<Campaign> {
  const reader = body?.getReader() ?? null;
  if (!reader) {
    throw new Error('No response body');
  }
  const decoder = new TextDecoder();
  let buffer = '';

  const processLine = (line: string): Campaign | 'pending' => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data: ')) {
      return 'pending';
    }
    const jsonStr = trimmed.replace(/^data:\s*/, '').trim();
    if (!jsonStr) {
      return 'pending';
    }
    return handleOnboardingSseData(jsonStr);
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (buffer.trim()) {
          for (const line of buffer.split(/\r?\n/)) {
            const out = processLine(line);
            if (out !== 'pending') {
              return out;
            }
          }
        }
        throw new Error('Connection closed before setup completed');
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const out = processLine(line);
        if (out !== 'pending') {
          return out;
        }
      }
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      // ignore
    }
  }
}

/**
 * Creates a campaign from onboarding selections and suggested companies.
 *
 * POST /v1/onboarding/signals/save — body: companies, instructions, prioritization_message.
 * Resolves with the campaign from the final `setup_complete` event (after GTM bootstrap).
 */
export async function saveOnboardingSignals(
  body: OnboardingSignalsSaveRequest
): Promise<Campaign> {
  const userSessionToken = getUserSessionCookie();
  const url = `${BASE_URL}/v1/onboarding/signals/save`;

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const message = await parseOnboardingErrorMessage(
      response,
      `Could not create campaign (${response.status})`
    );
    throw new Error(message);
  }

  return readOnboardingSaveSse(response.body);
}
